import { InjectionToken } from '@angular/core';
import { Store } from '@sama/app-state';
import { every, keyBy, mapValues } from '@sama/micro-dash';
import {
  isPointCloudOutput,
  isSensorFusionOutput,
  isVideoOutput,
} from '../../../cross-team-shared/misc-utils/output-utils';
import { getOutputHelper } from '../../../shared/output-helpers/get-output-helper';
import { AnswerOption } from '../../../team-apis/otterdam/answer-option';
import { Answer } from '../../../team-apis/otterdam/answers/answer';
import { AbstractOutput } from '../../../team-apis/otterdam/outputs/output';

export const outputInfoStoreToken = new InjectionToken<Store<OutputInfo>>(
  'output info store',
);

export class OutputInfo<
  T extends AbstractOutput = AbstractOutput,
  A extends Answer = Answer,
> {
  isVisible = false;
  canSaveDrafts = false;
  initialized = false;
  unsubmittableReason?: string;
  warningMessage?: string;

  /**
   * Can be different from the task's answers when the output is hidden. Then it
   * is removed from the task's answers but remains here.
   */
  answer?: A;
  trainingAnswer?: A;

  constructor(public output: T) {}

  static createBlanksByName(
    outputs: AbstractOutput[],
    isObjectEnabledProject = false,
  ): OutputInfoByName {
    if (!isObjectEnabledProject) {
      return this.createBlanksByNameObjectless(outputs);
    }
    const outputsByName = keyBy(outputs, (output) => output.name);
    return mapValues(outputsByName, (o) => new OutputInfo(o));
  }

  /** @deprecated used for old projects without objects */
  private static createBlanksByNameObjectless(
    outputs: AbstractOutput[],
  ): OutputInfoByName {
    const outputsByName = keyBy(outputs, (output) => output.name);
    const fusedAnswerOptions: AnswerOption[] = [];
    outputs.filter(isSensorFusionOutput).forEach((output) => {
      fusedAnswerOptions.push(...output.answer_options);
    });
    return mapValues(outputsByName, (o) => {
      if (isVideoOutput(o) || isPointCloudOutput(o)) {
        o.answer_options.unshift(...fusedAnswerOptions);
      }
      return new OutputInfo(o);
    });
  }
}

export interface OutputInfoByName {
  [name: string]: OutputInfo;
}

export function areDraftSavesEligible(outputInfoByName: OutputInfoByName) {
  const allInvisible = every(
    outputInfoByName,
    (outputInfo) => !outputInfo.isVisible,
  );

  if (allInvisible) {
    return false;
  }

  return every(
    outputInfoByName,
    (outputInfo) => !outputInfo.isVisible || outputInfo.canSaveDrafts,
  );
}

export function isBlank(outputInfo: OutputInfo) {
  return (
    !outputInfo.isVisible ||
    getOutputHelper(outputInfo.output.type).isAnswerBlank(outputInfo.answer)
  );
}

export function getValuesForOutputContext(output: AbstractOutput) {
  return {
    output_id: output.id,
    output_type: output.type,
  };
}

export function resetOutput(outputInfoStore: Store<OutputInfo>) {
  const output = outputInfoStore.state().output;
  const answer = getOutputHelper(output.type).getBlankAnswer(output);
  outputInfoStore('answer').set(answer);
}
